import React, { useMemo, useState } from "react";
import { ISorting } from "../../../interfaces/Components/ITable";
import { nextSort } from "./FilteredTable.utils";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import SortAsending from "../../Atomos/Icons/SortAsending";
import SortDecending from "../../Atomos/Icons/SortDecending";
import SortDefault from "../../Atomos/Icons/SortDefault";
import "../../../assets/css/table/Table.css";


export type SimpleSortableColumn<T> = {
  id: string;
  header: string;
  enableSorting?: boolean;
  cell?: (args: {
    row: T;
    value: unknown;
    rowIndex: number;
  }) => React.ReactNode;
  className?: string;
};

type SimpleSortableTableProps<T> = {
  columns: SimpleSortableColumn<T>[];
  data: T[];
  emptyMessage?: string;
  defaultSorting?: ISorting;
  getRowId?: (row: T, index: number) => string;
  className?: string;
};

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return -1;
  }
  if (b == null) {
    return 1;
  }

  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }

  const aStr = String(a).toLowerCase();
  const bStr = String(b).toLowerCase();
  return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: "base" });
}

function getRowValue<T extends Record<string, unknown>>(row: T, field: string): unknown {
  return row[field];
}

/**
 * Lightweight client-side table matching FilteredTableCore header/row styles.
 * Sorting only — no pagination, filters, column drag, or Excel.
 */
export default function SimpleSortableTable<T extends Record<string, unknown>>({
  columns,
  data,
  emptyMessage = "No data available.",
  defaultSorting,
  getRowId,
  className = "",
}: SimpleSortableTableProps<T>) {
  const { isStaffMode } = useStaffMode();
  const [sorting, setSorting] = useState<ISorting>(
    defaultSorting ?? { field: columns[0]?.id ?? "", order: "asc" },
  );

  const sortedData = useMemo(() => {
    if (!sorting.field) {
      return data;
    }
    const column = columns.find((c) => c.id === sorting.field);
    if (column?.enableSorting === false) {
      return data;
    }

    const direction = sorting.order === "asc" ? 1 : -1;
    return [...data].sort((left, right) => {
      const result = compareValues(
        getRowValue(left, sorting.field),
        getRowValue(right, sorting.field),
      );
      return result * direction;
    });
  }, [columns, data, sorting.field, sorting.order]);

  const handleSort = (field: string) => {
    const column = columns.find((c) => c.id === field);
    if (column?.enableSorting === false) {
      return;
    }
    setSorting((prev) => nextSort(prev, field));
  };

  const headerBgClass = isStaffMode
    ? "bg-accent-Default-dark"
    : "bg-primary-dark";

  return (
    <div className={`w-full min-w-0 overflow-hidden rounded-t-lg ${className}`.trim()}>
      <table className="table w-full">
        <thead>
          <tr>
            {columns.map((column, colIndex) => {
              const sortingEnabled = column.enableSorting !== false;
              const isSorted = sorting.field === column.id;
              const isFirst = colIndex === 0;
              const isLast = colIndex === columns.length - 1;
              const horizontalPaddingClass =
                isFirst && isLast
                  ? "px-4"
                  : isFirst
                    ? "pl-4 pr-1"
                    : isLast
                      ? "pl-1 pr-4"
                      : "px-1";

              return (
                <th
                  key={column.id}
                  scope="col"
                  className={`sticky top-0 z-10 ${headerBgClass} py-3 text-left text-sm font-medium text-white ${horizontalPaddingClass} ${column.className ?? ""}`}
                >
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="min-w-0 break-words">
                      {column.header}
                    </span>
                    {sortingEnabled && (
                      <button
                        type="button"
                        className="cursor-pointer flex items-center justify-center flex-shrink-0"
                        aria-label={`Sort ${column.header}`}
                        onClick={() => handleSort(column.id)}
                      >
                        {isSorted ? (
                          sorting.order === "asc" ? (
                            <SortAsending className="table-sort-icon" />
                          ) : (
                            <SortDecending className="table-sort-icon" />
                          )
                        ) : (
                          <span className="table-sort-icon">
                            <SortDefault />
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length || 1}
                className="p-6 text-center text-gray-font text-sm"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row, rowIndex) => (
              <tr
                key={getRowId?.(row, rowIndex) ?? String(row.id ?? rowIndex)}
                className="even:bg-gray-100 text-gray-font text-xs"
              >
                {columns.map((column, colIndex) => {
                  const value = getRowValue(row, column.id);
                  const isFirst = colIndex === 0;
                  const isLast = colIndex === columns.length - 1;
                  const horizontalPaddingClass =
                    isFirst && isLast
                      ? "px-4"
                      : isFirst
                        ? "pl-4 pr-1"
                        : isLast
                          ? "pl-1 pr-4"
                          : "px-1";

                  return (
                    <td
                      key={`${rowIndex}-${column.id}`}
                      className={`${horizontalPaddingClass} py-2 align-top break-words whitespace-normal ${column.className ?? ""}`}
                    >
                      {column.cell
                        ? column.cell({ row, value, rowIndex })
                        : value == null || value === ""
                          ? "—"
                          : String(value)}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
